import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { getMainDefinition } from "@apollo/client/utilities"
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  refreshTokens,
} from "client/components/auth/helpers"
import { QUERY_ACTIVE_QUESTION } from "client/packages/voting/votingHooks"
import merge from "deepmerge"
import { createClient } from "graphql-ws"
import fetch from "isomorphic-unfetch"
import { isEqual } from "lodash"
import { GetServerSidePropsResult } from "next"

import { GetChannelDocument, PluginsDocument } from "."

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__"

let apolloClient: ApolloClient<NormalizedCacheObject> = null

// Apollo's connectionCallback() runs after every websocket connection is initialized,
// including BOTH the initial page load AND afterward if the user's internet dies and comes back.
// In order to distinguish these two cases, we store these & check them during connectionCallback.
let pluginLinkPreviouslyConnected = false

export interface ConnectionParams {
  channelId?: string
  recordingId?: string
  sessionId?: string
}

export const initializeClientLinks = (
  client: ApolloClient<NormalizedCacheObject>,
  config: any,
  connectionParams: ConnectionParams,
) => {
  const { httpUri, wsUri } = config.apollo
  const { channelId, recordingId, sessionId } = connectionParams

  const httpLink = new HttpLink({
    uri:
      typeof channelId === "undefined"
        ? httpUri
        : `${httpUri}?channelId=${channelId}`,
    credentials: "include",
    // Use fetch() polyfill on the server
    fetch: typeof window === "undefined" && fetch,
    headers: {
      ...(channelId && {
        "X-S10-Channel-ID": channelId,
        "X-S10-Recording-ID": recordingId ?? "live",
      }),
      ...(sessionId && {
        "X-S10-Session-ID": sessionId,
      }),
    },
  })

  const authLink = setContext((_, { headers }) => {
    // The access token can either be a user or viewer token
    const accessToken = getAccessToken()
    const authorization = accessToken ? `Bearer ${accessToken}` : undefined
    if (authorization) {
      return {
        headers: {
          ...headers,
          authorization,
        },
      }
    }
    return { headers }
  })

  const retryLink = new RetryLink({
    delay: {
      initial: 300, // The number of milliseconds to wait before attempting the first retry.
      max: Infinity, // The maximum number of milliseconds that the link should wait for any retry
      jitter: true, // Whether delays between attempts should be randomized.
    },
    attempts: {
      max: 5, // The max number of times to try a single operation before giving up.
      // A predicate function that can determine whether a particular response should be retried.

      retryIf(error) {
        const status = error?.statusCode

        if (typeof status === "number") {
          // If we are unauthorized then the token needs to be refreshed and the
          // request retried. This should immediately resolve the issue
          if (status === 401) {
            return refreshTokens({
              tokenRefreshUri: config.stageten.tokenRefreshUri,
            })
              .then(() => true)
              .catch(() => false)
          }

          // If we got a response from the server and it's not a server problem
          // then there is no point in retrying the request
          if (status < 500) {
            return false
          }

          // In this case it's a server issue, continue to retry
          return true
        }

        // In this case we have no status. This could be a network outage for example.
        return !!error
      },
    },
  })

  const errorLink = onError(
    ({
      graphQLErrors,
      operation,
      forward,
    }): Observable<unknown> | null => {
      if (
        graphQLErrors &&
        graphQLErrors[0]?.extensions?.code === "UNAUTHENTICATED"
      ) {
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          return new Observable((observer) => {
            refreshTokens({
              tokenRefreshUri: config.stageten.tokenRefreshUri,
            })
              .then((refreshResponse) => {
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization:
                      `Bearer ${refreshResponse.accessToken}` || null,
                  },
                }))
              })
              .then(() => {
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                }

                // Retry last failed request
                forward(operation).subscribe(subscriber)
              })
              .catch((error) => {
                observer.error(error)
                clearTokens()
              })
          })
        }

        // If no refresh token is present, clear all tokens on auth error
        clearTokens()
      }

      return null
    },
  )

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const baseLink =
    typeof window !== "undefined"
      ? retryLink.split(
          // split based on operation type: websocket or http
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === "OperationDefinition" &&
              definition.operation === "subscription"
            )
          },
          new GraphQLWsLink(
            createClient({
              url: channelId ? `${wsUri}?channelId=${channelId}` : wsUri,
              connectionParams: {
                ...(channelId && {
                  "X-S10-Channel-ID": channelId,
                  "X-S10-Recording-ID": recordingId ?? "live",
                }),
                ...(sessionId && {
                  "X-S10-Session-ID": sessionId,
                }),
              },
              on: {
                connected: () => {
                  if (!pluginLinkPreviouslyConnected) {
                    pluginLinkPreviouslyConnected = true
                    return
                  }
                  console.log(
                    "Reconnected. Refetching plugin-service queries...",
                  )
                  // Refetch the data we missed when we had no internet connection. Specifically,
                  // Re-query everything that has an associated subscription on this ws link.
                  // IMPORTANT: every time we add a new subscription for this link,
                  // its query should be added to the list below.
                  client
                    .refetchQueries({
                      include: [
                        GetChannelDocument,
                        PluginsDocument,
                        QUERY_ACTIVE_QUESTION,
                      ],
                    })
                    .catch((error) => {
                      console.error("[Apollo refetchQueries error]", error)
                    })
                },
              },
            }),
          ),
          httpLink,
        )
      : retryLink.concat(httpLink)

  client.setLink(from([authLink, errorLink, baseLink]))
}

const create = (
  config: any,
  connectionParams: ConnectionParams,
): ApolloClient<NormalizedCacheObject> => {
  const client = new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Cart: {
          fields: {
            lines: {
              // If we ever support multiple carts at a time, we will need
              // to differentiate between them in the cache.
              keyArgs: [],
            },
          },
        },
        // Don't store GiveawayEvent objects in the cache.
        Giveaways_GiveawayEvent: {
          keyFields: false,
        },
      },
    }),
    connectToDevTools: true,
    ssrMode: typeof window === "undefined",
  })

  initializeClientLinks(client, config, connectionParams)

  return client
}

export function initApollo(
  config: any,
  connectionParams: ConnectionParams,
  initialState: any = {},
): ApolloClient<NormalizedCacheObject> {
  // Reuse client on the client-side if it already exists
  if (typeof window !== "undefined" && apolloClient) {
    return apolloClient
  }

  const newApolloClient = create(config, connectionParams)

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = newApolloClient.extract()

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: any, sourceArray: any) => [
        ...sourceArray,
        ...destinationArray.filter((d: any) =>
          sourceArray.every((s: any) => !isEqual(d, s)),
        ),
      ],
    })

    // Restore the cache with the merged data
    newApolloClient.cache.restore(data)
  }

  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === "undefined") {
    return newApolloClient
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = newApolloClient
  }

  return apolloClient
}

export function addApolloState<T extends GetServerSidePropsResult<{}>>(
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: T,
) {
  if ("props" in pageProps) {
    return {
      ...pageProps,
      props: {
        [APOLLO_STATE_PROP_NAME]: client.cache.extract(),
        ...pageProps.props,
      },
    }
  }

  return pageProps
}

export function useApollo(
  config: any,
  connectionParams: ConnectionParams,
  state: any,
) {
  return initApollo(config, connectionParams, state)
}
